import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Alert, IconButton, Tooltip, Divider, Grid
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import { ncTrackingService } from '../services/ncTrackingService';
import axios from 'axios';

const STATUS_COLORS = {
  open: 'error',
  in_correction: 'warning',
  resolved: 'success',
  correction_failed: 'default'
};

function NCCorrectionPanel({ entityType, entityId, onResubmitSuccess }) {
  const [ncs, setNCs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Correction dialog state
  const [correctionDialog, setCorrectionDialog] = useState({
    open: false,
    nc: null,
    notes: '',
    entityData: null,
    loadingEntity: false,
    editedValues: {}
  });
  
  // Resubmission state
  const [resubmitting, setResubmitting] = useState(false);

  useEffect(() => {
    if (entityType && entityId) {
      loadNCs();
    } else {
      console.warn('[NCCorrectionPanel] Missing entityType or entityId:', { entityType, entityId });
    }
    // eslint-disable-next-line
  }, [entityType, entityId]);

  const loadNCs = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[NCCorrectionPanel] Loading NCs for:', { entityType, entityId });
      const data = await ncTrackingService.getEntityNonConformances(entityType, entityId);
      console.log('[NCCorrectionPanel] Received NC data:', data);
      setNCs(data ?? []);
    } catch (err) {
      console.error('[NCCorrectionPanel] Error loading NCs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCorrection = async (nc) => {
    setCorrectionDialog({
      open: true,
      nc,
      notes: '',
      entityData: null,
      loadingEntity: true,
      editedValues: {}
    });

    // Load entity data to show source values
    try {
      const response = await axios.get(`/api/${getEntityRoute(entityType)}/${entityId}`);
      
      // Initialize edited values with current data
      const fieldName = extractFieldName(nc.description);
      const initialValues = {};
      if (fieldName && response.data[fieldName] !== undefined) {
        initialValues[fieldName] = response.data[fieldName];
      }
      if (response.data.quantity_value !== undefined) {
        initialValues.quantity_value = response.data.quantity_value;
      }
      if (response.data.emission_factor_value !== undefined) {
        initialValues.emission_factor_value = response.data.emission_factor_value;
      }

      setCorrectionDialog(prev => ({
        ...prev,
        entityData: response.data,
        editedValues: initialValues,
        loadingEntity: false
      }));
    } catch (err) {
      console.error('Error loading entity data:', err);
      setCorrectionDialog(prev => ({ ...prev, loadingEntity: false }));
    }
  };

  // Get the API route for entity type
  const getEntityRoute = (type) => {
    const routes = {
      'direct_emission': 'emissions',
      'allocation_execution': 'allocations/executions',
      'embedded_emission': 'embedded-emissions'
    };
    return routes[type] || 'emissions';
  };

  // Extract field name from description (e.g., "quantity_value must be > 0")
  const extractFieldName = (description) => {
    const match = description?.match(/^(\w+)\s/);
    return match ? match[1] : null;
  };

  // Handle field value changes
  const handleFieldChange = (fieldName, value) => {
    setCorrectionDialog(prev => ({
      ...prev,
      editedValues: {
        ...prev.editedValues,
        [fieldName]: value
      }
    }));
  };

  const handleSaveCorrection = async () => {
    try {
      setError(null);
      const { nc, notes, editedValues } = correctionDialog;
      
      if (!notes.trim()) {
        setError('Correction notes are required');
        return;
      }

      // Save the edited values to the database
      await axios.put(`/api/${getEntityRoute(entityType)}/${entityId}`, editedValues);

      // Mark NC as in_correction
      await ncTrackingService.startCorrection(nc.id, 'User', notes);
      
      // Close dialog and reload
      setCorrectionDialog({ open: false, nc: null, notes: '', entityData: null, loadingEntity: false, editedValues: {} });
      loadNCs();

      // Notify parent
      if (onResubmitSuccess) {
        onResubmitSuccess({ message: 'Correction saved successfully' });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResubmit = async () => {
    try {
      setResubmitting(true);
      setError(null);

      const correctionNotes = prompt('Describe the corrections made (optional):');
      
      const result = await ncTrackingService.resubmitCorrected(entityType, entityId, {
        correctedBy: 'User',
        correctionNotes: correctionNotes || 'Entity corrected and resubmitted',
        skipDVE: false  // Use dVE for verification
      });

      if (onResubmitSuccess) {
        onResubmitSuccess(result);
      }

      // Reload NCs
      loadNCs();
    } catch (err) {
      setError(err.message);
    } finally {
      setResubmitting(false);
    }
  };

  const openNCs = ncs.filter(nc => nc.status === 'open' || nc.status === 'in_correction');

  if (!entityType || !entityId) {
    return (
      <Alert severity="warning" variant="outlined">
        Missing entity information. Cannot load non-conformances.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (ncs.length === 0) {
    return (
      <Alert severity="success" variant="outlined">
        No non-conformances found for this entity
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Non-Conformances ({openNCs.length} open)
        </Typography>
        {openNCs.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={resubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleResubmit}
            disabled={resubmitting}
          >
            Resubmit Corrected Entity
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>dVE NC ID</TableCell>
              <TableCell>Principle</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ncs.map((nc) => (
              <TableRow key={nc.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  <Tooltip title={nc.dve_nc_id}>
                    <span>{nc.dve_nc_id?.slice(0, 8)}...</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{nc.principle || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={nc.severity || 'unknown'}
                    size="small"
                    color={nc.severity === 'high' || nc.severity === 'critical' ? 'error' : 'warning'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{nc.description || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={(nc.status || 'unknown').replace(/_/g, ' ')}
                    size="small"
                    color={STATUS_COLORS[nc.status]}
                  />
                </TableCell>
                <TableCell>
                  {nc.status === 'open' && (
                    <Tooltip title="Start Correction">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleStartCorrection(nc)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {nc.status === 'in_correction' && (
                    <Typography variant="caption" color="text.secondary">
                      In Progress
                    </Typography>
                  )}
                  {(nc.status === 'resolved' || nc.status === 'correction_failed') && (
                    <Typography variant="caption" color="text.secondary">
                      {nc.status === 'resolved' ? 'Resolved' : 'Failed'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Correction Dialog */}
      <Dialog
        open={correctionDialog.open}
        onClose={() => setCorrectionDialog({ open: false, nc: null, notes: '', entityData: null, loadingEntity: false })}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Fix Non-Conformance
          </Box>
        </DialogTitle>
        <DialogContent>
          {correctionDialog.nc && (
            <Box>
              {/* NC Details */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'warning.lighter' }}>
                <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                  <strong>Issue:</strong> {correctionDialog.nc.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Principle: {correctionDialog.nc.principle} | Severity: {correctionDialog.nc.severity}
                </Typography>
              </Paper>

              {/* Source Data Section */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InfoIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Source Data - Edit Values</Typography>
                </Box>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Entity: <strong>{entityType.replace(/_/g, ' ')}</strong> (ID: {entityId?.slice(0, 13)}...)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Update the incorrect values below to fix the non-conformance
                  </Typography>
                </Alert>

                {/* Editable field values */}
                {correctionDialog.loadingEntity ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={20} />
                  </Box>
                ) : correctionDialog.entityData && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Edit Field Values</Typography>
                    <Grid container spacing={2}>
                      {(() => {
                        const fieldName = extractFieldName(correctionDialog.nc.description);
                        const relevantFields = fieldName 
                          ? { [fieldName]: correctionDialog.entityData[fieldName] }
                          : {};
                        
                        // Add related context fields
                        if (correctionDialog.entityData.quantity_value !== undefined) {
                          relevantFields.quantity_value = correctionDialog.entityData.quantity_value;
                        }
                        if (correctionDialog.entityData.emission_factor_value !== undefined) {
                          relevantFields.emission_factor_value = correctionDialog.entityData.emission_factor_value;
                        }

                        return Object.entries(relevantFields).map(([key, originalValue]) => (
                          <Grid item xs={12} sm={6} key={key}>
                            <TextField
                              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              type="number"
                              fullWidth
                              size="small"
                              value={correctionDialog.editedValues[key] ?? ''}
                              onChange={(e) => handleFieldChange(key, e.target.value ? parseFloat(e.target.value) : null)}
                              error={correctionDialog.editedValues[key] === null || correctionDialog.editedValues[key] === 0}
                              helperText={
                                correctionDialog.editedValues[key] === null || correctionDialog.editedValues[key] === 0
                                  ? 'Value must be greater than 0'
                                  : `Original: ${originalValue ?? 'null'}`
                              }
                              InputProps={{
                                startAdornment: (
                                  <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                                    {key === 'quantity_value' ? 'kg' : key === 'emission_factor_value' ? 'kg CO2e' : ''}
                                  </Typography>
                                )
                              }}
                            />
                          </Grid>
                        ));
                      })()}
                    </Grid>
                  </Paper>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Correction Notes */}
              <TextField
                label="Correction Notes"
                fullWidth
                multiline
                rows={3}
                value={correctionDialog.notes}
                onChange={(e) => setCorrectionDialog({
                  ...correctionDialog,
                  notes: e.target.value
                })}
                placeholder="Describe what was corrected (e.g., 'Updated quantity_value from 0 to 100')..."
                required
                helperText="Document the changes you made to fix this issue"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCorrectionDialog({ open: false, nc: null, notes: '', entityData: null, loadingEntity: false, editedValues: {} })}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCorrection}
            variant="contained"
            disabled={!correctionDialog.notes.trim()}
            color="primary"
          >
            Save Correction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NCCorrectionPanel;
